import React, { Component, lazy } from 'react';
import lodashGet from 'lodash/get';
import randomColor from 'randomcolor';
import { Loader, Header, Tab, Menu, Label, Feed } from 'semantic-ui-react';
import { connect } from 'react-redux';

import * as actions from '../../../actions';
import { ArticleLink, BoardLink } from '../../Links';
import { DEFAULT_DAYS_AGO, USER_ARTICLES_LIMIT, USER_FANS_LIMIT } from '../../../constants';
import { TwentyFourHourToTwelveHourFormat, truncString, formatTimestamp, getPushIcon } from '../../Utils';
import { selectUserInfoResults } from '../../../selectors';

// Code splitting
import Activities from '../../Charts/Activities';
import Articles from '../common/Articles';
import Fans from './Fans';
import Info from './Info';
import Locations from '../../common/Locations';
import PushActivities from './PushActivities';
import RelatedIDsTable from './RelatedIDsTable';
import SearchArticles from '../Search/Articles';
import TagsChart from '../../Charts/TagsChart';
import TimeSeriesChart from '../../Charts/TimeSeriesChart';

const Pushes = ({ data, isMobile }) => (
  <Feed size="large">
    {
      data.map((push) => (
        <Feed.Event key={push.id}>
          <Feed.Content>
            <BoardLink id={push.board?.id}>
              <Label
                size="small"
                color="teal"
              >
                {push.board?.alias ?? push.board?.title}
                {' '}
                (
                {' '}
                {push.board?.title}
                {' '}
                )
              </Label>
            </BoardLink>
            <Feed.Summary>
              <ArticleLink id={push.article?.id}>
                {truncString(push.article?.title || '', isMobile ? 20 : 40)}
              </ArticleLink>
              <Feed.Date>
                {formatTimestamp(push.datetime)}
              </Feed.Date>
            </Feed.Summary>
            <Feed.Extra text>
              {
                push.floor && (
                  <span style={{ color: '#1abc9c' }}>
                    <b>
                      {push.floor}
                      樓
                    </b>
                  </span>
                )
              }
              {' '}
              {getPushIcon(push.tag, 'large')}
              {push.content}
            </Feed.Extra>
          </Feed.Content>
        </Feed.Event>
      ))
    }
  </Feed>
);

const renderActivitiesDetails = (data, isFetching, isMobile) => {
  const {
    articles,
    pushes,
  } = data;

  return (
    <Tab
      panes={[{
        menuItem: (
          <Menu.Item>
            推噓回文
            <Label>{pushes.length}</Label>
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane
            loading={isFetching}
            active={pushes.length > 0}
          >
            <Pushes
              data={pushes}
              isMobile={isMobile}
            />
          </Tab.Pane>
        ),
      }, {
        menuItem: (
          <Menu.Item>
            文章
            <Label>{articles.length}</Label>
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane
            loading={isFetching}
            active={articles.length > 0}
          >
            <SearchArticles data={articles} />
          </Tab.Pane>
        ),
      }]}
    />
  );
};

class User extends Component {
  constructor(props) {
    super(props);
    const {
      id,
      articles: {
        user,
        offset,
      },
    } = this.props;

    this.state = {
      id,
      offset: user === id ? offset : 0,
    };
  }

  componentDidMount() {
    if (this.state.id !== lodashGet(this.props.info, 'id')) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.id !== this.state.id) {
      this.fetchData();
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.id !== state.id) {
      return { id: props.id };
    }
    return null;
  }

  fetchData = async () => {
    const {
      authed,
      getRelatedUserIDs,
      getUserArticles,
      getUserFans,
      getUserInfo,
      getUserInterests,
      getUserLocations,
      getUserPushesStats,
      getUserRecentActivities,
    } = this.props;

    const {
      id,
      offset,
    } = this.state;

    const promises = [
      getUserArticles(id, USER_ARTICLES_LIMIT, offset),
      getUserInfo(id),
      getUserInterests(id),
      getUserPushesStats(id),
      getUserRecentActivities(id),
    ];

    if (authed) {
      promises.push(
        getRelatedUserIDs(id),
        getUserFans(id, USER_FANS_LIMIT),
        getUserLocations(id),
      );
    }

    const process = () => Promise.all(promises);

    await process();
  }

  fetchMoreArticles = (activePage) => {
    const {
      getUserArticles,
    } = this.props;

    const {
      id,
    } = this.state;

    const newOffset = (activePage - 1) * USER_ARTICLES_LIMIT;
    this.setState({
      offset: newOffset,
    }, () => getUserArticles(
      id,
      USER_ARTICLES_LIMIT,
      newOffset,
    ));
  }

  render() {
    const {
      articles,
      authed,
      browser,
      dailyActivitiesDetails,
      fans,
      info,
      interests,
      isFetching,
      locations,
      pushes,
      recentActivities,
      relatedIDs,
      searchTerm,
    } = this.props;

    const isMobile = browser.lessThan.medium;

    if (isFetching) {
      return (
        <Loader active inline="centered" />
      );
    }

    if (!info) {
      return (
        <Header as="h1" content="Oops..沒有找到使用者喔" color="grey" />
      );
    }

    const {
      id,
      offset,
    } = this.state;

    return (
      <div>
        <Info
          authed={authed}
          data={info}
          searchTerm={searchTerm}
          isMobile={isMobile}
        />
        <Activities
          computeValue={(count) => count.article + count.push}
          dailyActivitiesDetails={dailyActivitiesDetails}
          data={recentActivities.data.date}
          fetchDailyActivites={this.props.getUserDailyActivitiesDetails}
          isFetching={recentActivities.isFetching}
          isMobile={isMobile}
          renderActivitiesDetails={renderActivitiesDetails}
          userId={id}
          getTooltipDataAttrs={(value) => ({
            'data-tip': value.date
              ? `${value.date} -> 文章數: ${value.article}, 推/噓/回文數: ${value.push}`
              : '沒有任何活動喔',
          })}
        />
        <TimeSeriesChart
          data={recentActivities.data.times.map(({ d, count }) => ({
            indexBy: TwentyFourHourToTwelveHourFormat(d),
            value: count,
          }))}
          keys={['value']}
          indexBy="indexBy"
          isFetching={recentActivities.isFetching}
          isMobile={isMobile}
          color="#4285F4"
          title={`活動時間 (${DEFAULT_DAYS_AGO / 30}個月之內)`}
          tooltip={({ value }) => (
            <p>
              發文數 + 回文數:
              <b>{value}</b>
            </p>
          )}
          enableLabel={false}
        />
        <TagsChart
          title={`興趣取向 - 板 (${DEFAULT_DAYS_AGO / 30}個月之內)`}
          data={interests.data.pushesBoardsBreakdown.map((pb) => ({
            indexBy: `${pb.title} ${pb.alias ? `(${pb.alias})` : ''}`,
            value: pb.count,
            color: randomColor({ seed: pb.id }),
          }))}
          keys={['value']}
          indexBy="indexBy"
          valueScale="symlog"
          isMobile={isMobile}
          isFetching={interests.isFetching}
          tooltip={({ value, indexValue }) => (
            <p>
              {indexValue}
              :
              <b>{value}</b>
            </p>
          )}
        />
        <TagsChart
          title={`興趣取向 - 分類 (${DEFAULT_DAYS_AGO / 30}個月之內)`}
          data={interests.data.pushesCategoriesBreakdown.map(({ category, count }) => ({
            indexBy: isMobile ? truncString(category, 10) : category,
            value: count,
            color: randomColor({ seed: category }),
          }))}
          keys={['value']}
          indexBy="indexBy"
          valueScale="symlog"
          isMobile={isMobile}
          isFetching={interests.isFetching}
          tooltip={({ value, indexValue }) => (
            <p>
              {indexValue}
              :
              <b>{value}</b>
            </p>
          )}
        />
        <Articles
          data={articles.data}
          fetchFunc={this.fetchMoreArticles}
          isFetching={articles.isFetching}
          isMobile={isMobile}
          offset={offset}
          pageSize={USER_ARTICLES_LIMIT}
          showBoard
          showPushStats
          title={`所有文章 (${info.num_articles})`}
          total={info && Math.ceil(info.num_articles / USER_ARTICLES_LIMIT)}
        />
        <PushActivities
          data={pushes.data}
          isFetching={pushes.isFetching}
          isMobile={isMobile}
        />
        <Locations
          authed={authed}
          data={locations.data}
          isFetching={locations.isFetching}
          isMobile={isMobile}
        />
        <Fans
          authed={authed}
          data={fans.data}
          isFetching={fans.isFetching}
          isMobile={isMobile}
        />
        <RelatedIDsTable
          title={`多重帳號分析 (${DEFAULT_DAYS_AGO / 30}個月之內)`}
          authed={authed}
          data={relatedIDs.data}
          isFetching={relatedIDs.isFetching}
          isMobile={isMobile}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  articles: state.user.articles,
  browser: state.browser,
  dailyActivitiesDetails: state.user.dailyActivitiesDetails,
  fans: state.user.fans,
  info: selectUserInfoResults(state),
  interests: state.user.interests,
  isFetching: state.user.info.isFetching,
  locations: state.user.locations,
  pushes: state.user.pushesStats,
  recentActivities: state.user.recentActivities,
  relatedIDs: state.user.relatedIDs,
  searchTerm: state.search.q,
});

export default connect(mapStateToProps, actions)(User);
